import React, { Component, ErrorInfo, ReactNode } from 'react'

import ErrorPage from '../ErrorPage'

interface ErrorBoundaryProps {
  children: ReactNode
}

interface ErrorBoundaryState {
  hasError: boolean
}

/**
 * Catches render-time exceptions in the routed page tree and shows the branded
 * 500 error page instead of a blank white screen. The surrounding OOH
 * header/footer stay mounted, so the user always has a way out.
 *
 * Links here reload the page (hardLinks) rather than transitioning in-app,
 * since a crashed React tree may be in an inconsistent state.
 */
class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false }

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error('Uncaught error in render tree:', error, errorInfo)
  }

  render() {
    if (this.state.hasError) {
      return (
        <ErrorPage
          code='500'
          title='Something went wrong on our end'
          metaTitle='Something went wrong'
          message="An unexpected error interrupted this page. Try again in a moment, or head back home. If it keeps happening, please let us know."
          hardLinks
          showReload
        />
      )
    }

    return this.props.children
  }
}

export default ErrorBoundary
